import { __extends } from "tslib";
import { BaseAdapter } from '../core/adapters/base-adapter';
import { getShadowElement, Platform } from '@tyler-components-web/core';
import { QUANTITY_FIELD_CONSTANTS } from './quantity-field-constants';
var QuantityFieldAdapter = /** @class */ (function (_super) {
    __extends(QuantityFieldAdapter, _super);
    function QuantityFieldAdapter(component) {
        var _this = _super.call(this, component) || this;
        _this._rootElement = getShadowElement(component, QUANTITY_FIELD_CONSTANTS.selectors.ROOT);
        _this._incrementButtonSlot = getShadowElement(_this._component, QUANTITY_FIELD_CONSTANTS.selectors.INCREMENT_BUTTON_SLOT);
        _this._decrementButtonSlot = getShadowElement(_this._component, QUANTITY_FIELD_CONSTANTS.selectors.DECREMENT_BUTTON_SLOT);
        return _this;
    }
    QuantityFieldAdapter.prototype.addRootClass = function (name) {
        this._rootElement.classList.add(name);
    };
    QuantityFieldAdapter.prototype.removeRootClass = function (name) {
        this._rootElement.classList.remove(name);
    };
    QuantityFieldAdapter.prototype.inputHasAttribute = function (name) {
        if (!this.inputElement) {
            return false;
        }
        return this.inputElement.hasAttribute(name);
    };
    QuantityFieldAdapter.prototype.addIncrementButtonSlotListener = function (listener) {
        this._incrementButtonSlot.addEventListener('slotchange', listener);
    };
    QuantityFieldAdapter.prototype.removeIncrementButtonSlotListener = function (listener) {
        if (this._incrementButtonSlot) {
            this._incrementButtonSlot.removeEventListener('slotchange', listener);
        }
    };
    QuantityFieldAdapter.prototype.addDecrementButtonSlotListener = function (listener) {
        this._decrementButtonSlot.addEventListener('slotchange', listener);
    };
    QuantityFieldAdapter.prototype.removeDecrementButtonSlotListener = function (listener) {
        if (this._decrementButtonSlot) {
            this._decrementButtonSlot.removeEventListener('slotchange', listener);
        }
    };
    QuantityFieldAdapter.prototype.addTextFieldAttribute = function (name, value) {
        if (value === void 0) { value = ''; }
        if (this.textFieldComponent) {
            this.textFieldComponent.setAttribute(name, value);
        }
    };
    QuantityFieldAdapter.prototype.removeTextFieldAttribute = function (name) {
        if (this.textFieldComponent) {
            this.textFieldComponent.removeAttribute(name);
        }
    };
    QuantityFieldAdapter.prototype.addIncrementEventListener = function (event, callback) {
        this.incrementButton.addEventListener(event, callback);
    };
    QuantityFieldAdapter.prototype.removeIncrementEventListener = function (event, callback) {
        if (this.incrementButton) {
            this.incrementButton.removeEventListener(event, callback);
        }
    };
    QuantityFieldAdapter.prototype.addDecrementEventListener = function (event, callback) {
        this.decrementButton.addEventListener(event, callback);
    };
    QuantityFieldAdapter.prototype.removeDecrementEventListener = function (event, callback) {
        if (this.decrementButton) {
            this.decrementButton.removeEventListener(event, callback);
        }
    };
    QuantityFieldAdapter.prototype.addIncrementButtonAttribute = function (name, value) {
        if (value === void 0) { value = ''; }
        this.incrementButton.setAttribute(name, value);
    };
    QuantityFieldAdapter.prototype.removeIncrementButtonAttribute = function (name) {
        this.incrementButton.removeAttribute(name);
    };
    QuantityFieldAdapter.prototype.addDecrementButtonAttribute = function (name, value) {
        if (value === void 0) { value = ''; }
        this.decrementButton.setAttribute(name, value);
    };
    QuantityFieldAdapter.prototype.removeDecrementButtonAttribute = function (name) {
        this.decrementButton.removeAttribute(name);
    };
    QuantityFieldAdapter.prototype.setLabelAsRequired = function (required) {
        var labelElement = this._component.querySelector(QUANTITY_FIELD_CONSTANTS.selectors.LABEL);
        // Due to a Safari bug with ::slotted::after selectors, we need to manually append the required "asterisk" to the label text
        // https://bugs.webkit.org/show_bug.cgi?id=178237
        if (required && Platform.WEBKIT && labelElement && !labelElement.innerText.endsWith('*')) {
            var asterisk = document.createElement('span');
            asterisk.style.color = 'var(--mdc-theme-error)';
            asterisk.style.marginLeft = '1px';
            asterisk.textContent = '*';
            labelElement.appendChild(asterisk);
        }
    };
    QuantityFieldAdapter.prototype.increment = function () {
        var input = this.inputElement;
        if (!input) {
            return;
        }
        var curValue = +input.value;
        var newValue = curValue + this._inputStep;
        if (!!this._inputMax && this._inputMax < newValue) {
            newValue = this._inputMax;
        }
        this._setInputValue(newValue);
    };
    QuantityFieldAdapter.prototype.decrement = function () {
        var input = this.inputElement;
        if (!input) {
            return;
        }
        var curValue = +input.value;
        var newValue = curValue - this._inputStep;
        if (!!this._inputMin && this._inputMin > newValue) {
            newValue = this._inputMin;
        }
        this._setInputValue(newValue);
    };
    QuantityFieldAdapter.prototype.addInputDisabledAttributeChangeListener = function (callback) {
        this._setupInputDisabledAttributeMutationObserver(callback);
    };
    QuantityFieldAdapter.prototype.removeInputDisabledAttributeChangeListener = function () {
        this._cleanupInputDisabledAttributeMutationObserver();
    };
    QuantityFieldAdapter.prototype._setInputValue = function (value) {
        if (!this.inputElement) {
            return;
        }
        this.inputElement.value = "" + value;
        this.inputElement.dispatchEvent(new Event('input'));
        this.inputElement.dispatchEvent(new Event('change'));
    };
    QuantityFieldAdapter.prototype._isNullOrUndefinedOrEmpty = function (value) {
        return value === null || value === undefined || value === '';
    };
    QuantityFieldAdapter.prototype._setupInputDisabledAttributeMutationObserver = function (callback) {
        var _this = this;
        if (!this.inputElement) {
            return;
        }
        this._cleanupInputDisabledAttributeMutationObserver();
        var mutationCallback = function (mutationRecords) { return _this._inputDisabledAttributeMutationCallback(mutationRecords, callback); };
        this._inputAttributeMutationObserver = new MutationObserver(mutationCallback);
        var mutationOptions = {
            attributes: true,
            attributeOldValue: true,
            attributeFilter: [QUANTITY_FIELD_CONSTANTS.attributes.DISABLED]
        };
        this._inputAttributeMutationObserver.observe(this.inputElement, mutationOptions);
    };
    QuantityFieldAdapter.prototype._cleanupInputDisabledAttributeMutationObserver = function () {
        if (this._inputAttributeMutationObserver) {
            this._inputAttributeMutationObserver.disconnect();
            delete this._inputAttributeMutationObserver;
        }
    };
    QuantityFieldAdapter.prototype._inputDisabledAttributeMutationCallback = function (mutationRecords, callback) {
        for (var _i = 0, mutationRecords_1 = mutationRecords; _i < mutationRecords_1.length; _i++) {
            var mutationRecord = mutationRecords_1[_i];
            // Skip:
            //   - Non-attribute mutations.
            //   - Mutations without an attribute name.
            //   - Mutations without a matching attribute name.
            //   - Mutations that did not change the attribute value.
            if (mutationRecord.type !== 'attributes'
                || !mutationRecord.attributeName
                || mutationRecord.attributeName !== QUANTITY_FIELD_CONSTANTS.attributes.DISABLED
                || mutationRecord.target[mutationRecord.attributeName] === mutationRecord.oldValue) {
                continue;
            }
            callback();
        }
    };
    Object.defineProperty(QuantityFieldAdapter.prototype, "incrementButton", {
        get: function () {
            if (!this._incrementButton) {
                this._incrementButton = this._component.querySelector(QUANTITY_FIELD_CONSTANTS.selectors.INCREMENT_BUTTON);
            }
            return this._incrementButton;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(QuantityFieldAdapter.prototype, "decrementButton", {
        get: function () {
            if (!this._decrementButton) {
                this._decrementButton = this._component.querySelector(QUANTITY_FIELD_CONSTANTS.selectors.DECREMENT_BUTTON);
            }
            return this._decrementButton;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(QuantityFieldAdapter.prototype, "textFieldComponent", {
        get: function () {
            if (!this._tcwTextField) {
                this._tcwTextField = this._component.querySelector(QUANTITY_FIELD_CONSTANTS.selectors.TEXT_FIELD);
            }
            return this._tcwTextField;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(QuantityFieldAdapter.prototype, "inputElement", {
        get: function () {
            if (!this._inputElement) {
                this._inputElement = this._component.querySelector(QUANTITY_FIELD_CONSTANTS.selectors.INPUT);
            }
            return this._inputElement;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(QuantityFieldAdapter.prototype, "_inputStep", {
        get: function () {
            var input = this.inputElement;
            if (!input || this._isNullOrUndefinedOrEmpty(input.step)) {
                return 1;
            }
            return +input.step;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(QuantityFieldAdapter.prototype, "_inputMax", {
        get: function () {
            var input = this.inputElement;
            if (!input || this._isNullOrUndefinedOrEmpty(input.max)) {
                return null;
            }
            return +input.max;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(QuantityFieldAdapter.prototype, "_inputMin", {
        get: function () {
            var input = this.inputElement;
            if (!input || this._isNullOrUndefinedOrEmpty(input.min)) {
                return null;
            }
            return +input.min;
        },
        enumerable: true,
        configurable: true
    });
    return QuantityFieldAdapter;
}(BaseAdapter));
export { QuantityFieldAdapter };
